<template>
    <div>
        <div
            class="modal fade"
            id="modalCodeInput"
            tabindex="-1"
            aria-labelledby="modalCodeInputLabel"
            aria-hidden="true"
        >
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h3 class="modal-title" id="modalCodeInputLabel">
                            {{ $t("checkout.prepaid_box_modal_body_title") }}
                        </h3>
                        <button
                            type="button"
                            class="btn-close"
                            @click="closeModal"
                            aria-label="Close"
                        ></button>
                    </div>
                    <div class="modal-body">
                        <p>{{ $t("checkout.prepaid_box_modal_body_text") }}</p>
                        <input
                            type="text"
                            v-model="voucherCode"
                            v-mask="isLifestyle ? null : 'NNNN-NNNN-NNNN-NNNN'"
                            :placeholder="isLifestyle ? '*****' : '****-****-****-****'"
                            class="form-control text-center"
                            ref="voucherInput"
                            @keydown.enter="validateVoucher"
                        />
                        <div
                            v-if="alertMessage"
                            :class="alertClass"
                            class="alert mb-0 mt-3"
                            role="alert"
                        >
                            {{ alertMessage }}
                        </div>
                        <slot name="notification" v-if="isStockStatusAlert"></slot>
                    </div>
                    <div class="modal-footer">
                        <button
                            type="button"
                            class="btn btn-light"
                            @click="closeModal"
                        >
                            {{ $t("checkout.btn_cancel") }}
                        </button>
                        <button
                            type="button"
                            class="btn btn-primary position-relative"
                            @click="validateVoucher"
                            :disabled="isLoading || !isValidVoucherFormat"
                        >
                            <div class="position-absolute top-50 start-50 translate-middle">
                                <span
                                    class="spinner-border spinner-border-sm"
                                    :class="{ 'd-none': !isLoading }"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                            </div>

                            <div class="d-flex align-items-center" :class="{ 'opacity-0': isLoading }">
                                {{ $t("checkout.btn_prepaid_box_modal_validate") }}
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import shopware from "../../mixins/shopware";
import { Api } from "../../api/apiClient";
import { EventBus } from "../../api/eventBus.ts";
import _ from "lodash";
import { Modal } from "bootstrap";

export default {
    props: {
        isLifestyle: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            api: new Api(),
            alertMessage: "",
            alertClass: "",
            alertParams: {
                value: null,
                timestamp: Date.now()
            },
            isLoading: false,
            isStockStatusAlert: false,
            modal: null,
            voucherCode: "",
            retryCount: 0,
            maxRetries: 3
        };
    },
    mixins: [shopware],
    computed: {
        isValidVoucherFormat() {
            const unformattedCode = this.voucherCode.replace(/-/g, "");
            // Lifestyle accepts any format (min 3 chars), regular requires 12+ chars
            return this.isLifestyle
                ? unformattedCode.length >= 3
                : unformattedCode.length >= 12;
        }
    },
    mounted: function () {
        this.modal = new Modal(document.getElementById('modalCodeInput'));

        // Handle URL fragment for direct voucher entry
        if (window.location.hash === '#voucher') {
            this.openModal();
        }

        // Add listeners to voucher links
        document.querySelectorAll('[href*="#voucher"]').forEach((el) => {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                this.openModal();
            });
        });

        // Handle voucher modal events
        const modalElement = document.getElementById('modalCodeInput');
        if (modalElement) {
            modalElement.addEventListener('shown.bs.modal', () => {
                // Focus on the voucher input field when modal opens
                if (this.$refs.voucherInput) {
                    this.$refs.voucherInput.focus();
                }
            });
        }
    },
    watch: {
        'alertParams.timestamp': function() {
            if (this.alertParams.value?.productId) {
                this.isStockStatusAlert = true;

                setTimeout(() => {
                    if (typeof Livewire !== 'undefined') {
                        Livewire.dispatch('product-update', { id: this.alertParams.value.productId });
                    }
                }, 100);
            }
        }
    },
    methods: {
        openModal() {
            this.resetForm();
            this.modal.show();
        },
        closeModal() {
            this.modal.hide();
            this.resetForm();
        },
        resetForm() {
            this.voucherCode = "";
            this.alertMessage = "";
            this.alertClass = "";
            this.isStockStatusAlert = false;
            this.alertParams = {
                value: null,
                timestamp: Date.now()
            };
        },
        reload(delay = 2500) {
            // Fallback reload for edge cases where EventBus updates aren't sufficient
            setTimeout(() => {
                window.location = window.location.href.split('#')[0];
            }, delay);
        },
        async validateVoucher() {
            if (!this.isValidVoucherFormat || this.isLoading) {
                return;
            }

            try {
                this.isLoading = true;
                this.isStockStatusAlert = false;
                this.alertMessage = "";
                this.alertClass = "";
                this.alertParams = {
                    value: null,
                    timestamp: Date.now()
                };

                const unformattedCode = this.voucherCode.replace(/-/g, "");
                await this.api.updateContext();

                const response = await this.api.client.invoke(
                    "addPromotionItem post /checkout/cart/promotion",
                    {
                        code: unformattedCode,
                        languageId: this.api.currentLanguageId,
                    },
                );

                const errors = response.errors;
                if (errors && Object.keys(errors).length !== 0) {
                    const errorMessages = new Set();
                    let successMessage = null;

                    for (const key in errors) {
                        if (errors.hasOwnProperty(key)) {
                            if (key.startsWith('payment-method-blocked-')) {
                                continue;
                            }

                            const error = errors[key];
                            if (key.startsWith('promotion-discount-added-') || key.startsWith('faulty-voucher-product-combination')) {
                                successMessage = this.getErrorMessage(error);
                            } else {
                                this.alertParams = {
                                    value: error.parameters,
                                    timestamp: Date.now()
                                };
                                errorMessages.add(this.getErrorMessage(error));
                            }
                        }
                    }

                    if (successMessage && errorMessages.size === 0) {
                        this.alertMessage = successMessage;
                        this.alertClass = "alert-success";

                        EventBus.$emit("cart-updated", response);

                        // Track voucher application for analytics
                        this.trackVoucherApplied(response);

                        // Close modal after short delay to show success message
                        setTimeout(() => {
                            this.closeModal();
                        }, 1500);
                    } else {
                        this.alertMessage = [...errorMessages].join(" ");
                        this.alertClass = "alert-warning";

                        if (errorMessages.size === 0) {
                            this.reload();
                        }
                    }
                } else {
                    // Success with no errors
                    this.alertMessage = this.$t("checkout.voucher_applied_success");
                    this.alertClass = "alert-success";
                    EventBus.$emit("cart-updated", response);

                    // Track voucher application
                    this.trackVoucherApplied(response);

                    // Close modal after short delay to show success message
                    setTimeout(() => {
                        this.closeModal();
                    }, 1500);
                }
            } catch (error) {
                console.error("Error validating voucher:", error);

                // Implement retry logic for network errors
                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    this.alertMessage = this.$t("checkout.voucher_validation_retry");
                    this.alertClass = "alert-warning";

                    setTimeout(() => {
                        this.validateVoucher();
                    }, 1000);
                } else {
                    this.alertMessage = this.$t("checkout.prepaid_box_modal_msg_failed");
                    this.alertClass = "alert-warning";
                    this.retryCount = 0;
                }
            } finally {
                this.isLoading = false;
            }
        },

        trackVoucherApplied(response) {
            try {
                // Get the voucher code that was applied
                let voucherCode = this.voucherCode.replace(/-/g, "");

                // Get promotion information if available
                let promotionInfo = null;
                if (response.extensions && response.extensions["cart-promotions"] &&
                    response.extensions["cart-promotions"].addedCodes &&
                    response.extensions["cart-promotions"].addedCodes.length > 0) {
                    voucherCode = response.extensions["cart-promotions"].addedCodes[0];
                }

                // Push to data layer
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    'event': 'add_to_cart',
                    'type': 'voucher',
                    'items': [{
                        'item_id': voucherCode,
                        'item_name': 'voucher added',
                        'item_category': 'voucher',
                        'price': 0,
                        'quantity': 1,
                        'currency': 'EUR'
                    }]
                });
            } catch (error) {
                console.error("Error tracking voucher application:", error);
                // Non-critical error, don't disrupt the user flow
            }
        }
    },
};
</script>
